import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { BrandEnum } from '@infrastructure/brand.enum';
import { ModelGroupModel } from '@models/model-group';
import { BaseCustomizerComponent } from '@shared/base-customizer-component';
import { plainToClass } from 'class-transformer';
import { CustomizerMenuPartModel } from '../_models/customizer-menu-part';
import { MenuModel } from '../_models/menu';
import { MenuComponentModel } from '../_models/menu-component';
import * as Components from './../app/dynamic/components';
import { BrandModeService } from './brandmode.service';
import { StoreService } from './store.service';

interface ComponentsMap {
	[key: string]: typeof BaseCustomizerComponent;
}

const ComponentsTyped = Components as unknown as ComponentsMap;

@Injectable({
	providedIn: 'root'
})
export class MenuService {

	subMenuIsExpanded: boolean = false;

	constructor(private brandModeService: BrandModeService, private storeService: StoreService) { }

	public ConvertMenus(menus?: Array<MenuModel>): Array<CustomizerMenuPartModel> {
		const result: Array<CustomizerMenuPartModel> = [];

		menus?.forEach((menu) => {
			if (this.brandModeService.isTitanium && menu.SubMenus.length === 1) {
				menu = menu.SubMenus[0];
			}

			const resultMenu = this.getMenuItem(
				menu.Id,
				menu.Name,
				menu.TranslateName,
				menu.TranslateDescription,
				menu.Icon,
				null,
				null,
				menu.MenuComponentModels,
				menu.ContentGrouping,
				false,
				menu.MatchColor
			);

			const subMenus = menu.SubMenus?.filter(m => m.Brand == BrandEnum.TitaniumAndPrecious || m.Brand === this.brandModeService.currentBrandValue())

			resultMenu.subComponents = subMenus
				? subMenus.map(sm => this.getMenuItem(sm.Id, sm.Name, sm.TranslateName, sm.TranslateDescription, sm.Icon, [], resultMenu, sm.MenuComponentModels, sm.ContentGrouping, false, sm.MatchColor))
				: [];

			result.push(resultMenu);
		});

		if (this.brandModeService.isTitanium) {
			const caseMenuItem = this.getMenuItem(1, "Case", "CASE", null, "accessories", [], null, [], false, false, 0);

			result.push(caseMenuItem);
		}

		// TODO the concept of "ExtraInnerRim" does no longer exist. I suggest that the misc menu item is based on client/display mode
		if (!menus?.some(x => x.Name == "ExtraInnerrim")) {
			result.push(this.getMenuItem(0, "Reference", "MISCELLANEOUS", null, "reference", [], null, [], false, false, 0));
		}

		return result;
	}

	public getMenuItem(
		id: number,
		menuName: string,
		translateName: string,
		translateDecription: string | null,
		icon: string,
		subComponents: Array<CustomizerMenuPartModel> | null,
		parentMenu: CustomizerMenuPartModel | null,
		menuComponents: Array<MenuComponentModel>,
		contentGrouping: boolean,
		useForUpselling: boolean,
		matchColor: number
	): CustomizerMenuPartModel {
		return Object.assign(new CustomizerMenuPartModel(), {
			id,
			name: menuName,
			translateName,
			translateDescription: translateDecription,
			isVisited: false,
			isVisiting: false,
			icon,
			component: ComponentsTyped[`${menuName}Component`],
			subComponents,
			parentMenu,
			menuComponents,
			collapsed: true,
			contentGrouping,
			scrollPosition: null,
			useForUpselling,
			matchColor
		});
	}

	public async getMenu(modelgroup: ModelGroupModel | undefined): Promise<Array<MenuModel> | undefined> {
		const data = await this.getMenus();
		const result = data?.filter(m => m.ModelGroupUuid == modelgroup?.Uuid &&
			(m.Brand == BrandEnum.TitaniumAndPrecious || m.Brand === this.brandModeService.currentBrandValue())).map((x) => plainToClass(MenuModel, x));
		return result;
	}

	public async getMenuByUuids(modelgroupUuids: Array<string | undefined> | undefined): Promise<Array<MenuModel> | undefined> {
		const data = await this.getMenus();
		const result = data?.filter(m => modelgroupUuids?.some(id => m.ModelGroupUuid == id)).map((x) => plainToClass(MenuModel, x));
		return result;
	}

	public async getMenus() {
		const data = await this.storeService.getOrSetFromStore<Array<MenuModel>>(`${environment.dataUrl}/menus`);
		return data;
	}

	setSubMenuIsExpanded(isExpanded: boolean) {
		this.subMenuIsExpanded = isExpanded;
	}

}
